import { FC, useEffect, useMemo, useState } from 'react';
import { Backdrop, Button, CircularProgress, Modal, Rating } from '@mui/material';
import ReviewModal from '../Add';
import Table from 'src/components/Items/table';
import ActionForm from 'src/components/Items/ActionForm';
import DeleteModalForm from 'src/components/Items/DeleteModalForm';
import SecondTypeButton from 'src/components/Items/button/SecondTypeButton';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import useShowSnackbar from 'src/hooks/useShowSnackbar';
import { useActions } from 'src/hooks/useActions';
import { fromISOtoFormat } from 'src/common/utils/dataFormat';
import { IFeedback, IFeedbackFetch, IHostFetch } from 'src/models/IHost';

interface IProps {
  open: boolean;
  handleClose: () => void;
  hostId: number;
}

const ReviewListModal: FC<IProps> = ({ open, handleClose, hostId }) => {
  const { getHosts, clearReviewResult } = useActions();
  const hostList = useTypedSelector(state => state.host.hostList);
  const getHostsAll = useTypedSelector(state => state.host.result.all);
  const {
    feedback: { isLoading, result }
  } = useTypedSelector(state => state);
  const [selectedHost, setSelectedHost] = useState({} as IHostFetch);
  const [selectedReview, setSelectedReview] = useState({} as IFeedback);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  useShowSnackbar(isLoading, result, clearReviewResult, 'Feedback');

  useEffect(() => {
    (result.add || result.delete || result.update) && getHosts();
  }, [result]);

  useEffect(() => {
    getHostsAll &&
      hostList.map(host => {
        host.id === hostId && setSelectedHost(host);
      });
  }, [getHostsAll, hostId]);

  const handleOpenAdd = () => {
    setOpenAdd(true);
    setSelectedReview({
      order_id: 0,
      host_id: hostId,
      link: '',
      description: '',
      rates: 0
    });
  };

  const handleOpenEdit = (feedback: IFeedback) => {
    setOpenEdit(true);
    setSelectedReview(feedback);
  };

  const handleOpenDelete = (feedback: IFeedback) => {
    setOpenDelete(true);
    setSelectedReview(feedback);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Order Id',
        accessor: 'order_id',
        Cell: ({
          row: {
            original: { order_id }
          }
        }: any) => {
          return <div style={{ textAlign: 'center' }}>{order_id}</div>;
        },
        width: 150
      },
      {
        Header: 'Van Name',
        accessor: 'van_name',
        Cell: ({
          row: {
            original: { van_name }
          }
        }: any) => {
          return <>{van_name}</>;
        },
        width: 300
      },
      {
        Header: 'Rates',
        accessor: 'rates',
        Cell: ({
          row: {
            original: { rates }
          }
        }: any) => {
          return <Rating value={rates} precision={0.5} readOnly />;
        },
        width: 12
      },
      {
        Header: 'Description',
        accessor: 'description',
        Cell: ({
          row: {
            original: { description }
          }
        }: any) => {
          return <>{description}</>;
        },
        minWidth: 400
      },
      {
        Header: 'Link',
        accessor: 'link',
        Cell: ({
          row: {
            original: { link }
          }
        }: any) => {
          return (
            <div style={{ textAlign: 'center' }}>
              {link ? (
                <a
                  href={link}
                  target='_blank'
                  style={{ textDecoration: 'underline', color: 'rgb(0, 127, 255)' }}
                >
                  link
                </a>
              ) : (
                <></>
              )}
            </div>
          );
        },
        width: 70
      },
      {
        Header: 'Created at',
        accessor: 'created_at',
        Cell: ({
          row: {
            original: { created_at }
          }
        }: any) => {
          return (
            <div style={{ textAlign: 'center' }}>{fromISOtoFormat(created_at, 'MMMM yyyy')}</div>
          );
        },
        width: 200
      },
      {
        accessor: 'actions',
        Cell: ({ row: { original } }: any) => (
          <>
            <div className='flex_CC' style={{ justifyContent: 'right' }}>
              <ActionForm.IconSet
                title='feedback'
                classWrapper='action-wrapper'
                onEdit={() => handleOpenEdit(original)}
                onDelete={() => handleOpenDelete(original)}
              />
            </div>
          </>
        )
      }
    ],
    [selectedHost.feedback]
  );

  return (
    <>
      <Modal open={open} onClose={handleClose} BackdropComponent={Backdrop} closeAfterTransition>
        <div className='modal modal-1100'>
          {selectedHost.id ? (
            <>
              <div className='modal__title'>{selectedHost.first_name}'s Feedback List</div>
              <div className='col-2' style={{ marginTop: '30px' }}>
                <SecondTypeButton children='Cancel' onClick={handleClose} />
                <Button variant='contained' onClick={handleOpenAdd} children='Add feedback' />
              </div>
              {selectedHost.feedback.length ? (
                <div className='order-table'>
                  <Table.Base
                    data={selectedHost.feedback}
                    columns={columns}
                    class='react-table'
                    total={0}
                  />
                </div>
              ) : (
                <p className='modal__text'>There are no reviews yet</p>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <CircularProgress size={100} />
            </div>
          )}
        </div>
      </Modal>
      <ReviewModal review={selectedReview} open={openAdd} handleClose={() => setOpenAdd(false)} />
      <ReviewModal
        review={selectedReview}
        open={openEdit}
        handleClose={() => setOpenEdit(false)}
        isEdit
      />
      <DeleteModalForm
        id={(selectedReview as IFeedbackFetch).id as number}
        type='feedback'
        handleClose={() => setOpenDelete(false)}
        open={openDelete}
      />
    </>
  );
};
export default ReviewListModal;
